/**
 * What each line layer of the debug overlay draws (06 §8). Plain functions over a `LineBuffer`:
 * they read the sim and write segments, and allocate nothing.
 */

import { isFighter } from '../../sim/ctx';
import type { Statics } from '../../sim/statics';
import { BOMB, CARBINE, ENEMY, GRENADE, NOISE } from '../../sim/tuning';
import type { SimEvent, World } from '../../sim/types';
import type { Palette } from '../palette';
import type { LineBuffer } from './lineBuffer';

export type GroundY = (x: number, z: number) => number;

/** Only what is this close to the screen centre is drawn, metres: the view is about 36 x 28. */
export const DEBUG_RADIUS = 34;

const DEG = Math.PI / 180;
const near = (x: number, z: number, fx: number, fz: number, pad = 0): boolean =>
  Math.abs(x - fx) <= DEBUG_RADIUS + pad && Math.abs(z - fz) <= DEBUG_RADIUS + pad;

/** Footprint of every collider at ground level and again at its height, with a post between the two. */
export function drawColliders(
  out: LineBuffer, p: Palette, statics: Statics, fx: number, fz: number, groundY: GroundY,
): void {
  const solid = p.color('phosphor.0');
  const soft = p.color('water.hi');
  for (const c of statics.colliders) {
    if (!near(c.x, c.z, fx, fz, 8)) continue;
    const y = groundY(c.x, c.z) + 0.08;
    // Green stops a man; pale blue (wire, walk-through props) does not.
    const color = c.blocksMove ? solid : soft;
    if (c.shape === 'circle') {
      out.circle(c.x, y, c.z, c.r, 12, color);
      out.circle(c.x, y + c.h, c.z, c.r, 12, color);
      out.segment(c.x + c.r, y, c.z, c.x + c.r, y + c.h, c.z, color);
    } else {
      out.rect(c.x, y, c.z, c.hx, c.hz, color);
      out.rect(c.x, y + c.h, c.z, c.hx, c.hz, color);
      out.segment(c.x + c.hx, y, c.z + c.hz, c.x + c.hx, y + c.h, c.z + c.hz, color);
      out.segment(c.x - c.hx, y, c.z + c.hz, c.x - c.hx, y + c.h, c.z + c.hz, color);
    }
  }
}

/** Each fighting man's field of view: the two edges and the far arc, red while he has the ranger in sight. */
export function drawVision(
  out: LineBuffer, p: Palette, world: World, statics: Statics, fx: number, fz: number, groundY: GroundY,
): void {
  const calm = p.color('fire.2');
  const seeing = p.color('enemyAccent.0');
  const range = ENEMY.range[statics.def.timeOfDay];
  const half = (ENEMY.fovDeg / 2) * DEG;
  for (const e of world.enemies) {
    if (!isFighter(e) || !near(e.x, e.z, fx, fz, range)) continue;
    const y = groundY(e.x, e.z) + 0.12;
    const color = e.seesRanger ? seeing : calm;
    for (const side of [-1, 1]) {
      const a = e.facing + side * half;
      out.segment(e.x, y, e.z, e.x + Math.sin(a) * range, y, e.z + Math.cos(a) * range, color);
    }
    out.arc(e.x, y, e.z, range, e.facing - half, e.facing + half, 10, color);
    // Inside this he is seen at once, whatever the meter says.
    out.arc(e.x, y, e.z, ENEMY.instantRange, e.facing - half, e.facing + half, 6, color, 2);
  }
}

/** A metre-wide bar over each head: the dark track, and the detection meter filling it left to right. */
export function drawDetection(
  out: LineBuffer, p: Palette, world: World, fx: number, fz: number, groundY: GroundY,
): void {
  const track = p.color('ink.0');
  const rising = p.color('playerAccent.0');
  const full = p.color('enemyAccent.0');
  for (const e of world.enemies) {
    if (!isFighter(e) || e.detection <= 0 || !near(e.x, e.z, fx, fz)) continue;
    const y = groundY(e.x, e.z) + 2.5;
    const k = Math.min(1, e.detection);
    out.segment(e.x - 0.5, y, e.z, e.x + 0.5, y, e.z, track);
    out.segment(e.x - 0.5, y + 0.07, e.z, e.x - 0.5 + k, y + 0.07, e.z, k >= 1 ? full : rising);
  }
}

/** The path each man is walking, from where he stands through the waypoints still ahead of him. */
export function drawPaths(out: LineBuffer, p: Palette, world: World, fx: number, fz: number, groundY: GroundY): void {
  const color = p.color('water.hi');
  for (const e of world.enemies) {
    if (!isFighter(e) || e.pathIndex >= e.path.length || !near(e.x, e.z, fx, fz, 20)) continue;
    let px = e.x;
    let pz = e.z;
    for (let i = e.pathIndex; i < e.path.length; i++) {
      const node = e.path[i];
      if (!node) break;
      out.segment(px, groundY(px, pz) + 0.15, pz, node[0], groundY(node[0], node[1]) + 0.15, node[1], color);
      px = node[0];
      pz = node[1];
    }
  }
}

/* ------------------------------------------------------------------------------ noise */

/** A noise the overlay heard about: where, how far it carries, and how long ago (seconds). */
export interface NoiseRing {
  x: number;
  z: number;
  radius: number;
  age: number;
}

export const NOISE_SECONDS = 1.2;

/**
 * The sim has no `noise` event, so rings are read off the events that make one, with the radius
 * the sim uses for it (tuning.ts). Footsteps are silent here: they raise no event at all.
 */
export function noiseOf(e: SimEvent): { x: number; z: number; radius: number } | null {
  switch (e.type) {
    case 'shot':
      return { x: e.x, z: e.z, radius: e.fromPlayer ? CARBINE.noise : NOISE.enemyShot };
    case 'explosion':
      return { x: e.x, z: e.z, radius: e.big ? BOMB.noise : GRENADE.noise };
    case 'bark':
      return { x: e.x, z: e.z, radius: NOISE.shout };
    case 'mine':
    case 'iceBreak':
      return { x: e.x, z: e.z, radius: NOISE.shout };
    default:
      return null;
  }
}

/** A ring at the distance the noise carries, breaking up as it ages: solid, then dashed, then dotted. */
export function drawNoise(out: LineBuffer, p: Palette, rings: readonly NoiseRing[], groundY: GroundY): void {
  const color = p.color('paper.0');
  for (const ring of rings) {
    const t = ring.age / NOISE_SECONDS;
    out.circle(ring.x, groundY(ring.x, ring.z) + 0.2, ring.z, ring.radius, 48, color, t < 0.3 ? 1 : t < 0.65 ? 2 : 4);
  }
}
